'use client';

import { type FunctionComponent, type ReactNode, type Ref, useState } from 'react';
import { clsx } from 'clsx';
import DisclosureRightIcon from '../../../icons/react/icon-disclosure-right';
import { type Interaction, Interactive } from '../../../util/interaction';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import type { DrawerProps } from '../../dialog/drawer/drawer';
import { BaseLink, type LinkElementType } from '../../link/base-link';
import { Skeleton } from '../../loading/skeleton';
import { Typography } from '../../typography';
import styles from './list-item.module.scss';

const defaultRightIcon = <DisclosureRightIcon aria-hidden height={16} />;

type RequiredDrawerProps = Pick<DrawerProps, 'trigger'>;

interface ListItemDrawer<IDrawerProps extends RequiredDrawerProps = any> {
  Component: FunctionComponent<IDrawerProps>;
  props?: Omit<IDrawerProps, keyof RequiredDrawerProps>;
}

export interface ListItem {
  className?: string;
  drawer?: ListItemDrawer;
  hideExpandIcon?: boolean;
  icon?: ReactNode;
  id: string;
  interaction?: Interaction;
  LinkElement?: LinkElementType;
  onClick?: VoidFunction;
  pills?: ReactNode[];
  reducedMotion?: boolean;
  ref?: Ref<HTMLLIElement>;
  rightIcon?: ReactNode;
  subtitle?: ReactNode;
  title?: ReactNode;
  to?: string;
  value?: ReactNode;
  valueAlign?: 'left' | 'right' | 'center' | 'auto';
}

type Feature = Exclude<keyof ListItem, 'id' | 'LinkElement' | 'onClick' | 'to' | 'drawer'>;

export function buildSkeletonListItems(count: number, features: Feature[] = ['title']) {
  return new Array(count).fill(null).map((_, i) => {
    const item: ListItem = {
      id: `skeleton-${i}`,
      icon: features.includes('icon') ? <Skeleton style={{ height: 16, width: 16 }} type="rect" /> : undefined,
      pills: features.includes('pills') ? [<Skeleton key="pill" style={{ height: 20, width: 90 }} type="rect" />] : undefined,
      rightIcon: features.includes('rightIcon') ? <Skeleton style={{ height: 16, width: 16 }} type="rect" /> : undefined,
      subtitle: features.includes('subtitle') ? <Skeleton charCount={20} fillParent style={{ marginTop: 4 }} type="line" /> : undefined,
      title: features.includes('title') ? <Skeleton charCount={15} fillParent type="line" /> : undefined,
      value: features.includes('value') ? <Skeleton charCount={15} fillParent type="line" /> : undefined,
    };

    return item;
  });
}

export function Item({
  className,
  drawer,
  hideExpandIcon,
  icon,
  id,
  interaction,
  LinkElement = 'a',
  onClick,
  pills,
  reducedMotion,
  ref,
  rightIcon = defaultRightIcon,
  subtitle,
  title,
  to,
  value,
  valueAlign = 'right',
}: ListItem) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const hasText = Boolean(title || subtitle);
  const hasRightContent = Boolean(pills?.length || value || to || onClick || interaction || (rightIcon && rightIcon !== defaultRightIcon));

  const base = (
    <HorizontalStack align="space-between" blockAlign="center" className={styles.content} gap={2} wrap="nowrap">
      <HorizontalStack blockAlign="center" gap={2}>
        {icon}
        {hasText ? (
          <VerticalStack>
            {title ? (
              <Typography lineHeight="1.125rem" size="0.875rem">
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography lineHeight="1.125rem" size="0.875rem" tone="neutral-dk">
                {subtitle}
              </Typography>
            ) : null}
          </VerticalStack>
        ) : null}
      </HorizontalStack>

      {hasRightContent || drawer ? (
        <HorizontalStack blockAlign="center" gap={2}>
          {value ? <div className={clsx(styles.value, styles[`align-${valueAlign}`])}>{value}</div> : null}
          {pills}
          {!hideExpandIcon && (to || onClick || drawer || rightIcon || interaction) ? rightIcon : null}
        </HorizontalStack>
      ) : null}
    </HorizontalStack>
  );

  const baseClassName = clsx(styles.listItem, reducedMotion && styles.reducedMotion, icon ? styles.hasIcon : undefined, className);

  if (to) {
    return (
      <li className={baseClassName} id={id} ref={ref}>
        <BaseLink className={styles.container} LinkElement={LinkElement} onClick={onClick} to={to}>
          {base}
        </BaseLink>
      </li>
    );
  }

  if (onClick) {
    return (
      <li className={baseClassName} id={id} ref={ref}>
        <button className={styles.container} onClick={onClick} type="button">
          {base}
        </button>
      </li>
    );
  }

  if (drawer) {
    return (
      <drawer.Component
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        trigger={
          <li className={clsx(baseClassName, styles.drawerActivator)} id={id} ref={ref}>
            <div className={styles.container}>{base}</div>
          </li>
        }
        {...drawer.props}
      />
    );
  }

  if (interaction) {
    const BaseElement = !['link', 'button'].includes(interaction.type) ? 'button' : 'div';

    return (
      <li className={baseClassName} id={id} ref={ref}>
        <Interactive className={styles.interactiveActivator} {...interaction}>
          <BaseElement className={styles.container}>{base}</BaseElement>
        </Interactive>
      </li>
    );
  }

  return (
    <li className={baseClassName} id={id} ref={ref}>
      <div className={styles.container}>{base}</div>
    </li>
  );
}
